import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Download, ArrowRight, AlertCircle, Sparkles, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Lands here after the user clicks the verification link in their email.
 * Auto-triggers the PDF download via the secure one-time token.
 */
const FrameworkDownloadSuccessPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const downloadToken = params.get("download");
  const [downloading, setDownloading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (downloadToken) {
      // Trigger the PDF download once on landing
      const triggerDownload = async () => {
        try {
          setDownloading(true);
          const url = `${BACKEND_URL}/api/framework-download/file?token=${encodeURIComponent(downloadToken)}`;
          // Open in a new tab so the success page remains visible
          window.open(url, "_blank", "noopener");
          setDone(true);
        } catch (e) {
          setError("Could not start the download automatically. Please use the button below.");
        } finally {
          setDownloading(false);
        }
      };
      const t = setTimeout(triggerDownload, 600);
      return () => clearTimeout(t);
    }
  }, [downloadToken]);

  if (!downloadToken) {
    return (
      <PublicLayout>
        <section className="py-24 bg-[#F8FAFC]">
          <div className="container mx-auto px-4 max-w-xl text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-[#1E3A5F] mb-3">Verification link issue</h1>
            <p className="text-gray-600 mb-8">This link is missing a download token. Please request the framework again.</p>
            <Button onClick={() => navigate("/resources")} className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-3 rounded-xl">
              Back to Resources
            </Button>
          </div>
        </section>
      </PublicLayout>
    );
  }

  const downloadUrl = `${BACKEND_URL}/api/framework-download/file?token=${encodeURIComponent(downloadToken)}`;

  return (
    <PublicLayout>
      <section className="py-20 bg-gradient-to-br from-[#FFFBEB] via-white to-[#F8FAFC]" data-testid="framework-success-hero">
        <div className="container mx-auto px-4 sm:px-6 max-w-2xl">
          <div className="bg-white rounded-3xl shadow-xl border border-[#FDB813]/30 p-10 text-center">
            <div className="w-16 h-16 bg-[#FDB813] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-9 h-9 text-white" />
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">Email Verified</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">
              Your <span className="text-[#FDB813]">CNESS Framework</span> is ready
            </h1>
            <p className="text-gray-600 leading-relaxed mb-8">
              Thank you for confirming. Your download should start automatically — if it doesn't, use the button below.
            </p>

            {error && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm mb-6">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="framework-success-download-btn"
                className="inline-flex items-center justify-center bg-[#1E3A5F] hover:bg-[#152a45] text-white px-6 py-3 rounded-xl font-semibold"
              >
                {downloading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Download className="w-5 h-5 mr-2" />}
                Download the PDF
              </a>
              <Button
                onClick={() => navigate("/certification")}
                variant="outline"
                className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white rounded-xl"
              >
                Start Certification <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <p className="text-xs text-gray-500 mt-8">
              We've also emailed a copy of the download link to you for safe-keeping. Have questions? Reply to that email or write to{" "}
              <a href="mailto:inquiries@cness.co" className="text-[#1E3A5F] underline">inquiries@cness.co</a>.
            </p>

            {done && (
              <p className="sr-only" data-testid="framework-success-state">Download initiated</p>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default FrameworkDownloadSuccessPage;
